import React, { type ComponentPropsWithoutRef } from 'react';
import { type Control, useWatch } from 'react-hook-form';
import { SwitchCase, useCombinedRefs } from '@toss/react';
import { useTranslation } from 'next-i18next';
import { StaticRichTextRenderer } from '@/shared/features/rich-text';
import { cn } from '@/utils';
import { type BuildSchemaInputType } from '../../form/schema';
import { validateQuizItem } from '../../form/schema/BuildQuizListSchema';
import { useQuizInView } from '../focus/useQuizInView';
import { QuizItemToolsDropdown } from './QuizItemToolsDropdown';
import { QuizTypeIcon } from './QuizTypeIcon';
import { useQuizSliderFocusScrollRef } from './useQuizSliderFocusScrollRef';
import { useSliderItemSortable } from './useSliderItemSortable';
import { WithSliderItemContextMenu } from './WithSliderItemContextMenu';
interface QuizSliderItemProps extends ComponentPropsWithoutRef<'div'> {
  index: number;
  onClick: () => void;
  onCopy: () => void;
  onDelete: (index: number, eventProperties?: Record<string, string>) => void;
  control: Control<BuildSchemaInputType>;
  isEditMode: boolean;
}
export const QuizSliderItem = ({
  index,
  onClick,
  control,
  isEditMode,
  onCopy,
  onDelete,
  ...props
}: QuizSliderItemProps) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    setNodeRef,
    attributes,
    listeners,
    style
  } = useSliderItemSortable({
    id: props.id as string
  });
  const scrollRef = useQuizSliderFocusScrollRef(props.id as string);
  const combinedRef = useCombinedRefs<HTMLDivElement>(setNodeRef, scrollRef);
  const isFocused = isEditMode;
  const isInView = useQuizInView(index);
  const quiz = useWatch({
    control,
    name: `quizList.${index}`
  });
  const isValid = validateQuizItem(quiz).success;

  // Determine if question text is empty or placeholder
  const hasQuestionText = quiz.question && quiz.question.trim().length > 0;
  const questionText = hasQuestionText ? quiz.questionRichText || quiz.question : t('quiz.slider.placeholder');
  const isPlaceholder = !hasQuestionText;
  return <WithSliderItemContextMenu onCopy={onCopy} onDelete={() => onDelete(index, {
    position: 'sidebar_context_menu'
  })} data-sentry-element="WithSliderItemContextMenu" data-sentry-component="QuizSliderItem" data-sentry-source-file="QuizSliderItem.tsx">
      <div ref={combinedRef} {...attributes} {...listeners} onClick={onClick} style={style} className={cn('flex w-full items-start gap-xs text-left cursor-pointer')}>
        <div className="flex w-[20px] shrink-0 items-center justify-end px-4xs py-2xs">
          <span className="text-body-sm-semibold text-neutral">
            {index + 1}
          </span>
        </div>

        <div className={cn('group/card relative flex min-w-0 flex-1 flex-col gap-xs rounded-md bg-white p-xs transition-all duration-200', 'shadow-[inset_0_0_0_1px_theme(borderColor.alternative)]', !isValid && 'bg-alternative', isInView && 'shadow-[inset_0_0_0_2px_theme(borderColor.opacity.black.4)]', 'hover:shadow-[inset_0_0_0_2px_theme(borderColor.strong.assistive)]', isFocused && '!shadow-[inset_0_0_0_2px_theme(borderColor.violet.400)]')}>
          <div className="flex items-center gap-xs px-3xs py-4xs">
            <QuizItemToolsDropdown onCopy={onCopy} onDelete={() => onDelete(index, {
            position: 'slider_dropdown'
          })} data-sentry-element="QuizItemToolsDropdown" data-sentry-source-file="QuizSliderItem.tsx" />
            {/* Icon */}
            <div className="flex shrink-0 self-start">
              <SwitchCase value={quiz.type} caseBy={{
              multiple: <QuizTypeIcon.Multiple />,
              subjective: <QuizTypeIcon.Subjective />,
              ox: <QuizTypeIcon.Ox />,
              essay: <QuizTypeIcon.Essay />
            }} data-sentry-element="SwitchCase" data-sentry-source-file="QuizSliderItem.tsx" />
            </div>

            {/* Question Text */}
            <StaticRichTextRenderer as="div" text={questionText} className={cn('line-clamp-3 min-w-0 flex-1 text-body-sm-semibold select-none', isPlaceholder ? 'text-disabled' : 'text-neutral')} data-sentry-element="StaticRichTextRenderer" data-sentry-source-file="QuizSliderItem.tsx" />
          </div>
        </div>
      </div>
    </WithSliderItemContextMenu>;
};